import { useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

export const PEOPLE_PATH = "/grading/people";

export const PEOPLE_TABS = [
  "district-admins",
  "staff",
  "students",
  "invitations",
] as const;
export type PeopleTab = (typeof PEOPLE_TABS)[number];

/** Query parameters a filter change keeps; every other filter change sends the list back to page 1. */
const NON_FILTER_KEYS = ["district", "page", "person", "student"] as const;

/** Filters that live in the URL (contracts/people-console.md §Route). `role` maps to the API's `roles`. */
export const PEOPLE_FILTER_KEYS = [
  "q",
  "school",
  "class",
  "role",
  "status",
  "source",
  "grade",
  "missing",
] as const;
export type PeopleFilterKey = (typeof PEOPLE_FILTER_KEYS)[number];

export interface PeopleUrlState {
  /** The `:tab` segment as given; it may be missing or not a tab this viewer can see. */
  tab?: string;
  /** Required for platform admins. */
  district?: string;
  q: string;
  school?: string;
  class?: string;
  role?: string;
  status?: string;
  source?: string;
  grade?: string;
  /** "Missing scoring data" pill on the Students tab. */
  missing: boolean;
  /** Zero-based; the URL holds it one-based and omits page 1. */
  page: number;
  /** Open staff panel. */
  person?: string;
  /** Open student panel. */
  student?: string;
}

type PatchValue = string | number | boolean | null | undefined;
export type PeopleUrlPatch = Partial<
  Record<PeopleFilterKey | (typeof NON_FILTER_KEYS)[number], PatchValue>
>;

const serialize = (key: string, value: PatchValue): string | null => {
  if (value === null || value === undefined || value === false) return null;
  if (key === "page") {
    const page = Number(value);
    return page > 0 ? String(page + 1) : null;
  }
  if (value === true) return "1";
  const text = String(value);
  return text === "" ? null : text;
};

/** Reads and writes the People page's tab, district and filters in the URL. */
export const usePeopleUrlState = () => {
  const { tab } = useParams<{ tab?: string }>();
  const { search } = useLocation();
  const history = useHistory();

  const state = useMemo<PeopleUrlState>(() => {
    const params = new URLSearchParams(search);
    const get = (key: string) => params.get(key) || undefined;
    const page = parseInt(params.get("page") ?? "", 10);
    return {
      tab,
      district: get("district"),
      q: params.get("q") ?? "",
      school: get("school"),
      class: get("class"),
      role: get("role"),
      status: get("status"),
      source: get("source"),
      grade: get("grade"),
      missing: params.get("missing") === "1",
      page: Number.isFinite(page) && page > 1 ? page - 1 : 0,
      person: get("person"),
      student: get("student"),
    };
  }, [tab, search]);

  /** Link to a tab, keeping only the district. */
  const tabHref = useCallback(
    (next: PeopleTab) =>
      `${PEOPLE_PATH}/${next}${
        state.district
          ? `?${new URLSearchParams({ district: state.district })}`
          : ""
      }`,
    [state.district],
  );

  /** Merges a patch into the query. A filter change returns to the first page. */
  const setFilters = useCallback(
    (patch: PeopleUrlPatch) => {
      // The live location, not the last render's: two patches in one tick must both apply.
      const params = new URLSearchParams(history.location.search);
      const changesFilter = Object.keys(patch).some(
        (key) => !(NON_FILTER_KEYS as readonly string[]).includes(key),
      );
      if (changesFilter) params.delete("page");
      Object.entries(patch).forEach(([key, value]) => {
        const text = serialize(key, value);
        if (text === null) params.delete(key);
        else params.set(key, text);
      });
      const query = params.toString();
      history.replace({ search: query ? `?${query}` : "" });
    },
    [history],
  );

  /** Clears every filter, keeping the district. */
  const clearFilters = useCallback(() => {
    history.replace({
      search: state.district
        ? `?${new URLSearchParams({ district: state.district })}`
        : "",
    });
  }, [history, state.district]);

  /** Picks a district (platform admins); the other filters belonged to the old one. */
  const setDistrict = useCallback(
    (district: string | undefined) => {
      history.push({
        search: district ? `?${new URLSearchParams({ district })}` : "",
      });
    },
    [history],
  );

  return { ...state, tabHref, setFilters, clearFilters, setDistrict };
};

export default usePeopleUrlState;
